import React, { useEffect, useMemo, useState } from "react";
import api, { formatINR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { NotebookPen, Plus, Trash2 } from "lucide-react";
import { toast, Toaster } from "sonner";
import { useAuth } from "../../contexts/AuthContext";

const KIND_LABELS = {
    other_income:   { label: "Other income",      hint: "Bank interest, rent received, scrap sale, etc.",        section: "P&L" },
    other_expense:  { label: "Other expense",     hint: "Rent, salary, utilities, courier, professional fees…",  section: "P&L" },
    asset_opening:  { label: "Asset (opening)",   hint: "Cash on hand, fixed assets at start of books.",          section: "Balance Sheet" },
    liability:      { label: "Liability",         hint: "Sundry creditors, loans, GST payable.",                  section: "Balance Sheet" },
    capital:        { label: "Capital introduced", hint: "Owner's contribution into the business.",               section: "Balance Sheet" },
    drawings:       { label: "Drawings",          hint: "Owner withdrew money from the business.",                section: "Balance Sheet" },
};

const todayStr = new Date().toISOString().slice(0, 10);

export default function AccountsJournal() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [filter, setFilter] = useState("all");
    const [openNew, setOpenNew] = useState(false);
    const [form, setForm] = useState({ date: todayStr, kind: "other_expense", account: "", amount: "", narration: "" });
    const [busy, setBusy] = useState(false);
    const isAdmin = user?.role === "admin" || user?.role === "editor";

    const load = async () => {
        const params = filter !== "all" ? { kind: filter } : {};
        const { data } = await api.get("/accounts/journal", { params });
        setData(data);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

    const submit = async () => {
        if (!form.account.trim()) return toast.error("Account label is required");
        const amt = parseFloat(form.amount);
        if (!amt || amt <= 0) return toast.error("Amount must be positive");
        setBusy(true);
        try {
            await api.post("/accounts/journal", { ...form, amount: amt });
            toast.success("Journal entry saved");
            setOpenNew(false);
            setForm({ date: todayStr, kind: form.kind, account: "", amount: "", narration: "" });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        } finally { setBusy(false); }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this journal entry permanently?")) return;
        try {
            await api.delete(`/accounts/journal/${id}`);
            toast.success("Deleted");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    };

    const totalsByKind = data?.totals_by_kind || {};

    return (
        <div data-testid="accounts-journal">
            <Toaster position="top-center" />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700 flex items-center gap-2"><NotebookPen className="w-3.5 h-3.5" />Tally-style</div>
                    <h1 className="font-serif text-4xl text-slate-900 mt-2">Journal</h1>
                    <p className="text-sm text-stone-600 mt-2">Manual ledger entries that feed the Profit & Loss and Balance Sheet. Use this for rent, salaries, owner's capital, opening balances, and so on.</p>
                </div>
                <Button onClick={() => setOpenNew(true)} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="journal-new"><Plus className="w-4 h-4 mr-2" />New entry</Button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 text-xs">
                <Pill active={filter === "all"} onClick={() => setFilter("all")} testId="jf-all">All</Pill>
                {Object.entries(KIND_LABELS).map(([k, v]) => (
                    <Pill key={k} active={filter === k} onClick={() => setFilter(k)} testId={`jf-${k}`}>
                        {v.label} {totalsByKind[k] ? `· ${formatINR(totalsByKind[k])}` : ""}
                    </Pill>
                ))}
            </div>

            <div className="bg-white border border-sky-100 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="bg-sky-50 border-b border-sky-100">
                            <Th>Date</Th><Th>Type</Th><Th>Account</Th>
                            <Th cls="text-right">Amount</Th><Th>Narration</Th><Th>By</Th><Th></Th>
                        </tr>
                    </thead>
                    <tbody>
                        {!data ? (
                            <tr><td colSpan={7} className="p-8 text-center text-stone-500">Loading…</td></tr>
                        ) : !data.rows.length ? (
                            <tr><td colSpan={7} className="p-8 text-center text-stone-500">No journal entries yet. Click "New entry" to add one.</td></tr>
                        ) : data.rows.map((j) => (
                            <tr key={j.id} className="border-b border-stone-100" data-testid={`journal-row-${j.id}`}>
                                <td className="p-2 font-mono text-xs">{j.date}</td>
                                <td className="p-2">
                                    <span className="text-[10px] uppercase tracking-widest text-stone-700 bg-stone-100 px-2 py-0.5">{KIND_LABELS[j.kind]?.label || j.kind}</span>
                                </td>
                                <td className="p-2 font-medium text-slate-900">{j.account}</td>
                                <td className="p-2 text-right font-mono">{formatINR(j.amount)}</td>
                                <td className="p-2 text-xs text-stone-600 max-w-md truncate" title={j.narration}>{j.narration || "—"}</td>
                                <td className="p-2 text-xs text-stone-500">{j.created_by_name || "—"}</td>
                                <td className="p-2 text-right">
                                    {isAdmin && (
                                        <button onClick={() => remove(j.id)} className="text-red-700 hover:text-red-800" data-testid={`journal-del-${j.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogContent className="rounded-none max-w-md">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">New journal entry</DialogTitle></DialogHeader>
                    <div className="space-y-3 mt-2">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Date</Label>
                                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="je-date" />
                            </div>
                            <div>
                                <Label className="text-[10px] uppercase tracking-widest text-stone-500">Type</Label>
                                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                                    <SelectTrigger className="rounded-none border-stone-300 mt-1" data-testid="je-kind"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(KIND_LABELS).map(([k, v]) => (
                                            <SelectItem key={k} value={k}>{v.label} <span className="text-stone-400">· {v.section}</span></SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <p className="text-[11px] text-stone-500 italic">{KIND_LABELS[form.kind]?.hint}</p>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Account label *</Label>
                            <Input value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} placeholder="e.g. Rent, Salaries, Owner's Capital" className="rounded-none border-stone-300 mt-1" data-testid="je-account" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Amount (₹) *</Label>
                            <Input type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-none border-stone-300 mt-1" data-testid="je-amount" />
                        </div>
                        <div>
                            <Label className="text-[10px] uppercase tracking-widest text-stone-500">Narration</Label>
                            <Textarea rows={2} value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} placeholder="Optional note for the auditor" className="rounded-none border-stone-300 mt-1" data-testid="je-narration" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-none">Cancel</Button>
                        <Button onClick={submit} disabled={busy} className="rounded-none bg-sky-700 hover:bg-sky-800 text-white" data-testid="je-save">{busy ? "Saving…" : "Save entry"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Th = ({ children, cls = "" }) => <th className={`p-2 text-xs uppercase tracking-widest text-stone-500 ${cls || "text-left"}`}>{children}</th>;
const Pill = ({ active, onClick, children, testId }) => (
    <button onClick={onClick} className={`px-3 py-1.5 border ${active ? "border-sky-700 bg-sky-700 text-white" : "border-stone-300 text-stone-700 hover:border-sky-700"}`} data-testid={testId}>
        {children}
    </button>
);
